import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  Commune,
  CommuneCreateCommand,
  CommuneDeleteCommand,
  CommuneUpdateCommand
} from "../../model/Commune";
import {FormComposable, FormComposableField, PopupFormAction} from "@tracabois/components";
import {useFormComposable} from "@komune-io/g2-composable";
import {PlatformSectionForm} from "@tracabois/template/src/platform/PlatformSectionForm";

export interface CommuneFormProps {
  item?: Partial<Commune>
  onCreate: (item: CommuneCreateCommand) => Promise<boolean> | boolean
  onUpdate: (item: CommuneUpdateCommand) => Promise<boolean> | boolean
  onDelete: (item: CommuneDeleteCommand) => Promise<boolean> | boolean
  onCancel: () => void
  readonly?: boolean
}

export const CommuneForm = (props: CommuneFormProps) => {
  const {
    item,
    onCreate,
    onUpdate,
    onDelete,
    onCancel,
    readonly = false,
  } = props
  const [feedback, setFeedback] = useState<boolean | undefined>(undefined)
  useEffect(() => {
    setFeedback(undefined)
  }, [onCreate, onUpdate])

  const onSubmitMemoized = useCallback(
    async (values: CommuneCreateCommand | CommuneUpdateCommand) => {
      if (!item?.id) {
        const feedback = await onCreate({
          ...item,
          ...values,
        })
        setFeedback(feedback)
        return feedback
      } else {
        const feedback = await onUpdate({
          ...item,
          ...values,
          id: item.id
        })
        setFeedback(feedback)
        return feedback
      }
    },
    [onCreate, onUpdate, item]
  )

  const details = useMemo(
    (): FormComposableField[] => [
      {
        key: 'name',
        name: 'name',
        type: 'textField',
        label: 'Nom de la commune',
        defaultValue: item?.name,
        params: {
          disabled: readonly
        },
        validator: (value?: string) => {
          const trimmed = (value ?? '').trim()
          if (!trimmed) return 'Vous devez renseigner le nom' as string
          return undefined
        }
      },
      {
        key: 'codeInsee',
        name: 'codeInsee',
        type: 'textField',
        label: 'Code INSEE',
        defaultValue: item?.codeInsee,
        params: {
          disabled: readonly
        },
        validator: (value?: string) => {
          const trimmed = (value ?? '').trim()
          if (!trimmed) return 'Vous devez renseigner le code INSEE' as string
          return undefined
        }
      },
      {
        key: 'department',
        name: 'department',
        type: 'textField',
        label: 'Département',
        defaultValue: item?.department,
        params: {
          disabled: readonly
        },
        validator: (value?: string) => {
          const trimmed = (value ?? '').trim()
          if (!trimmed) return 'Vous devez renseigner le département' as string
          return undefined
        }
      },
      {
        key: 'region',
        name: 'region',
        type: 'textField',
        label: 'Région',
        defaultValue: item?.region,
        params: {
          disabled: readonly
        }
      },
      {
        key: 'latitude',
        name: 'latitude',
        type: 'textField',
        label: 'Latitude',
        defaultValue: item?.latitude?.toString(),
        params: {
          disabled: readonly,
          type: 'number'
        }
      },
      {
        key: 'longitude',
        name: 'longitude',
        type: 'textField',
        label: 'Longitude',
        defaultValue: item?.longitude?.toString(),
        params: {
          disabled: readonly,
          type: 'number'
        }
      },
      {
        key: 'geoJson',
        name: 'geoJson',
        type: 'textField',
        label: 'GeoJSON',
        defaultValue: item?.geoJson,
        params: {
          disabled: readonly,
          multiline: true,
          rows: 6
        },
        validator: (value?: string) => {
          if (value && value.trim()) {
            try {
              JSON.parse(value)
            } catch {
              return 'Le GeoJSON doit être un JSON valide' as string
            }
          }
          return undefined
        }
      }
    ],
    [readonly, item]
  )

  const formState = useFormComposable({
    onSubmit: onSubmitMemoized
  })
  const handleOnDelete = item?.id ? (() => onDelete({id: item.id!!})) : undefined

  return (
    <PlatformSectionForm>
      <FormComposable
        fields={details}
        formState={formState}
      />
      <PopupFormAction
        formState={formState}
         onCancel={onCancel}
         feedback={feedback}
         readonly={readonly}
         isCreation={!item?.id}
        onDelete={handleOnDelete}
      />
    </PlatformSectionForm>
  )
}
